import React, { useState, useRef, useCallback } from "react";
import Papa from "papaparse";
import { Receipt, FileText, User, BedDouble, Stethoscope, Plus, HeartPulse, Activity, Upload, CheckCircle, XCircle, AlertTriangle, X, Loader2 } from "lucide-react";
import { UnifiedRecord, SystemMetrics } from "../../../types";

interface Props {
  records: UnifiedRecord[];
  metrics: SystemMetrics | null;
  loading: boolean;
}

export function BillingDashboard({ records, loading }: Props) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Filter patient records
  const patientRecords = records.filter(r => !r.patient_id.startsWith("BED-ONLY-"));
  const patientsWithCharges = patientRecords.filter(r => (r.total_charge || 0) > 0).length;
  const totalBilledAmount = patientRecords.reduce((sum, r) => sum + (r.total_charge || 0), 0);
  const totalAppointments = patientRecords.reduce((sum, r) => sum + (r.doctor_visits || 0), 0);
  const surgeryRecords = patientRecords.filter(r => (r.surgery_charge || 0) > 0).length;

  // File Upload State
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) processFile(droppedFile);
  }, []);

  const processFile = (file: File) => {
    setFile(file);
    setUploadSuccess(false);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0 && results.data.length === 0) {
          setError(results.errors[0].message);
        } else {
          setParsed(results.data);
          setError(null);
        }
      },
      error: (err) => setError(err.message),
    });
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFile(null);
    setParsed(null);
    setError(null);
    setUploadSuccess(false);
  };

  const handleSubmit = async () => {
    if (!parsed) return;
    setSubmitting(true);
    // Mock processing delay
    await new Promise(r => setTimeout(r, 800));
    setSubmitting(false);
    setUploadSuccess(true);
  };

  const isLoaded = !!file && !error;
  const hasError = !!error;

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl mx-auto pb-12">
      
      {/* Header */}
      <div className="flex items-center gap-4 border-b border-white/5 pb-4">
        <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center border border-teal-500/30 shadow-[0_0_15px_rgba(20,184,166,0.3)]">
          <Receipt className="w-6 h-6 text-teal-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white tracking-wide">Patient Billing, Appointments & Charges</h2>
          <p className="text-gray-400 text-sm">Demo pricing only: the amounts below are approximate example charges in INR and are fully editable for your hospital's actual tariff.</p>
        </div>
      </div>

      {/* Approximate Hospital Price List */}
      <div className="bg-[#040d1a]/80 backdrop-blur-xl border border-white/5 rounded-3xl p-6">
        <h4 className="text-sm font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2 mb-4">
          <FileText className="w-4 h-4 text-teal-400" /> Approximate Hospital Price List
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white/5 p-4 rounded-2xl border border-white/5 text-center hover:bg-white/10 transition-colors cursor-default">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">General OPD</p>
            <p className="text-lg font-bold text-white">₹300</p>
          </div>
          <div className="bg-white/5 p-4 rounded-2xl border border-white/5 text-center hover:bg-white/10 transition-colors cursor-default">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Specialist</p>
            <p className="text-lg font-bold text-white">₹600</p>
          </div>
          <div className="bg-white/5 p-4 rounded-2xl border border-white/5 text-center hover:bg-white/10 transition-colors cursor-default">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Follow-up / Revisit</p>
            <p className="text-lg font-bold text-white">₹250</p>
          </div>
          <div className="bg-white/5 p-4 rounded-2xl border border-white/5 text-center hover:bg-white/10 transition-colors cursor-default">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">General Ward / day</p>
            <p className="text-lg font-bold text-white">₹1,500</p>
          </div>
          <div className="bg-white/5 p-4 rounded-2xl border border-white/5 text-center hover:bg-white/10 transition-colors cursor-default">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">ICU / day</p>
            <p className="text-lg font-bold text-white">₹6,000</p>
          </div>
        </div>
        <p className="text-[10px] text-gray-500 mt-4 text-center">Example machine charges: ventilator ₹3,000/day, oxygen support ₹800/day, dialysis ₹4,500/session, CT ₹4,000, MRI ₹7,000. Surgery examples range from ₹25,000 to ₹1,50,000+ depending on procedure.</p>
      </div>

      {/* Spreadsheet Upload Section */}
      <div className="bg-[#040d1a]/80 backdrop-blur-xl border border-white/5 rounded-3xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-bold text-teal-400 uppercase tracking-wider flex items-center gap-2">
            <Upload className="w-4 h-4" /> Bulk Patient Charges Upload
          </h4>
        </div>
        
        <div
          className={`relative rounded-2xl border-2 transition-all duration-200 ${
            isDragging
              ? "border-teal-500/60 bg-teal-500/8 scale-[1.01]"
              : isLoaded
              ? "border-green-500/40 bg-green-500/5"
              : hasError
              ? "border-red-500/40 bg-red-500/5"
              : "border-teal-500/30 bg-white/3 hover:bg-white/5"
          } cursor-pointer p-6`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => !isLoaded && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) processFile(file);
            }}
          />

          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
              isLoaded ? "bg-green-500/15" : hasError ? "bg-red-500/15" : "bg-white/5"
            }`}>
              {isLoaded ? (
                <CheckCircle className="w-6 h-6 text-green-400" />
              ) : hasError ? (
                <XCircle className="w-6 h-6 text-red-400" />
              ) : (
                <FileText className="w-6 h-6 text-teal-400" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-sm text-teal-400">Patient Charges Log (CSV)</h3>
                {isLoaded && (
                  <button onClick={handleClear} className="text-gray-600 hover:text-red-400 transition-colors p-1">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-gray-500 text-xs mt-0.5">Upload a CSV containing patient IDs and line-item charges</p>

              {!file && !hasError && (
                <div className="mt-3 flex items-center gap-2">
                  <Upload className="w-3.5 h-3.5 text-gray-600" />
                  <span className="text-gray-600 text-xs">Drag & drop a CSV file, or click to browse</span>
                </div>
              )}

              {isLoaded && parsed && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <span className="text-white text-xs font-medium truncate max-w-[200px]">{file?.name}</span>
                  <span className="text-green-400 text-xs font-mono">{parsed.length} rows parsed ✓</span>
                </div>
              )}

              {hasError && (
                <div className="mt-3 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-red-400 text-xs">{error}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {isLoaded && !uploadSuccess && (
           <div className="mt-4 flex justify-end">
             <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center justify-center min-w-[200px] gap-2.5 px-6 py-2.5 rounded-xl bg-gradient-teal text-white font-semibold text-sm transition-all duration-200 hover:shadow-glow disabled:opacity-40"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                ) : (
                  <><Upload className="w-4 h-4" /> Apply Bulk Charges</>
                )}
              </button>
           </div>
        )}
        
        {uploadSuccess && (
           <div className="mt-4 rounded-xl border border-green-500/30 bg-green-500/10 p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <p className="text-green-400 text-sm font-medium">Successfully processed {parsed?.length} line-item charges and updated patient totals!</p>
           </div>
        )}
      </div>

      {/* Manual Input Forms (UI Mockup) */}
      <div className="space-y-4">
        
        {/* Doctor Appointment / Revisit */}
        <div className="bg-gradient-to-r from-teal-500/5 to-transparent border border-teal-500/20 rounded-2xl p-5">
          <h4 className="text-sm font-bold text-teal-400 flex items-center gap-2 mb-4">
            <User className="w-4 h-4" /> Doctor Appointment / Revisit
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
            <input type="text" placeholder="Patient ID *" className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-teal-500" />
            <input type="text" placeholder="Patient Name" className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-teal-500" />
            <select className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-teal-500">
              <option>General Physician</option>
              <option>Specialist</option>
              <option>Super Specialist</option>
            </select>
            <select className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-teal-500">
              <option>Select Doctor *</option>
              <option>Dr. Sharma</option>
              <option>Dr. Gupta</option>
            </select>
            <select className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-teal-500">
              <option>New Appointment</option>
              <option>Follow-up</option>
            </select>
            <input type="date" className="bg-[#071428] border border-white/10 text-gray-400 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-teal-500" />
          </div>
          <div className="flex items-center gap-3">
             <input type="number" placeholder="Charge (₹)" defaultValue={300} className="w-32 bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-teal-500" />
             <button className="flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-bold py-2 px-4 rounded-lg text-sm transition-colors shadow-[0_0_10px_rgba(20,184,166,0.2)]">
               <Plus className="w-4 h-4" /> Add Appointment Charge
             </button>
          </div>
        </div>

        {/* Bed / Ward Charges */}
        <div className="bg-gradient-to-r from-blue-500/5 to-transparent border border-blue-500/20 rounded-2xl p-5">
          <h4 className="text-sm font-bold text-blue-400 flex items-center gap-2 mb-4">
            <BedDouble className="w-4 h-4" /> Bed / Ward Charges
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <input type="text" placeholder="Patient ID *" className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
            <select className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500">
              <option>General Ward</option>
              <option>Private Room</option>
              <option>ICU / MICU</option>
            </select>
            <input type="number" placeholder="Days *" defaultValue={1} className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
            <input type="number" placeholder="Rate / Day (₹)" className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
          </div>
          <button className="flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white font-bold py-2 px-4 rounded-lg text-sm transition-colors shadow-[0_0_10px_rgba(59,130,246,0.2)]">
            <Plus className="w-4 h-4" /> Add Bed Charge
          </button>
        </div>

        {/* Machine / Equipment Usage */}
        <div className="bg-gradient-to-r from-indigo-500/5 to-transparent border border-indigo-500/20 rounded-2xl p-5">
          <h4 className="text-sm font-bold text-indigo-400 flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4" /> Machine / Equipment Usage
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <input type="text" placeholder="Patient ID *" className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500" />
            <select className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500">
              <option>Ventilator</option>
              <option>Oxygen Support</option>
              <option>Dialysis Machine</option>
            </select>
            <input type="number" placeholder="Duration (Days/Sessions) *" defaultValue={1} className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500" />
            <input type="number" placeholder="Charge / Unit (₹)" className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500" />
          </div>
          <button className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white font-bold py-2 px-4 rounded-lg text-sm transition-colors shadow-[0_0_10px_rgba(99,102,241,0.2)]">
            <Plus className="w-4 h-4" /> Add Machine Charge
          </button>
        </div>

        {/* Medicines Purchased */}
        <div className="bg-gradient-to-r from-rose-500/5 to-transparent border border-rose-500/20 rounded-2xl p-5">
          <h4 className="text-sm font-bold text-rose-400 flex items-center gap-2 mb-4">
            <HeartPulse className="w-4 h-4" /> Medicines Purchased
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <input type="text" placeholder="Patient ID *" className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-rose-500" />
            <input type="text" placeholder="Medicine Name *" className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-rose-500" />
            <input type="number" placeholder="Quantity *" defaultValue={1} className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-rose-500" />
            <input type="number" placeholder="Unit Cost (₹)" className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-rose-500" />
          </div>
          <button className="flex items-center gap-2 bg-rose-500 hover:bg-rose-400 text-white font-bold py-2 px-4 rounded-lg text-sm transition-colors shadow-[0_0_10px_rgba(244,63,94,0.2)]">
            <Plus className="w-4 h-4" /> Add Purchased Medicine
          </button>
        </div>

        {/* Surgery / Procedure */}
        <div className="bg-gradient-to-r from-amber-500/5 to-transparent border border-amber-500/20 rounded-2xl p-5">
          <h4 className="text-sm font-bold text-amber-400 flex items-center gap-2 mb-4">
            <Stethoscope className="w-4 h-4" /> Surgery / Procedure
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            <input type="text" placeholder="Patient ID *" className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500" />
            <input type="text" placeholder="Procedure Name *" className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500" />
            <input type="text" placeholder="Surgeon / Doctor" className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500" />
            <input type="date" className="bg-[#071428] border border-white/10 text-gray-400 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500" />
            <input type="number" placeholder="Surgery Charge (₹)" className="bg-[#071428] border border-white/10 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500" />
          </div>
          <button className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold py-2 px-4 rounded-lg text-sm transition-colors shadow-[0_0_10px_rgba(245,158,11,0.2)]">
            <Plus className="w-4 h-4" /> Add / Update Surgery
          </button>
        </div>

      </div>

      {/* Patient-wise Total Amount Table */}
      <div className="bg-[#040d1a]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-6 mt-8">
        <h4 className="text-lg font-bold text-white flex items-center gap-2 mb-6">
          <Receipt className="w-5 h-5 text-teal-400" /> Patient-wise Total Amount
        </h4>
        
        {/* KPI Summaries */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/5 border-l-4 border-blue-500 rounded-r-xl p-4">
            <p className="text-2xl font-bold text-white">{patientsWithCharges}</p>
            <p className="text-xs text-gray-400 uppercase tracking-wider">Patients with charges</p>
          </div>
          <div className="bg-white/5 border-l-4 border-teal-500 rounded-r-xl p-4">
            <p className="text-2xl font-bold text-white">₹{totalBilledAmount.toLocaleString()}</p>
            <p className="text-xs text-gray-400 uppercase tracking-wider">Total billed amount</p>
          </div>
          <div className="bg-white/5 border-l-4 border-indigo-500 rounded-r-xl p-4">
            <p className="text-2xl font-bold text-white">{totalAppointments}</p>
            <p className="text-xs text-gray-400 uppercase tracking-wider">Appointments</p>
          </div>
          <div className="bg-white/5 border-l-4 border-rose-500 rounded-r-xl p-4">
            <p className="text-2xl font-bold text-white">{surgeryRecords}</p>
            <p className="text-xs text-gray-400 uppercase tracking-wider">Surgery records</p>
          </div>
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-left text-sm text-gray-300 whitespace-nowrap">
            <thead className="bg-[#071428] text-teal-400 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 font-semibold border-b border-white/10">Patient ID</th>
                <th className="px-4 py-3 font-semibold border-b border-white/10">Name</th>
                <th className="px-4 py-3 font-semibold border-b border-white/10 text-center">Appointments</th>
                <th className="px-4 py-3 font-semibold border-b border-white/10 text-right">Beds (₹)</th>
                <th className="px-4 py-3 font-semibold border-b border-white/10 text-right">Labs/Machines (₹)</th>
                <th className="px-4 py-3 font-semibold border-b border-white/10 text-right">Surgery (₹)</th>
                <th className="px-4 py-3 font-semibold text-gray-300 border-b border-white/10 text-right">Total Charge</th>
                <th className="px-4 py-3 font-semibold text-emerald-400 border-b border-white/10 text-right">Paid Amount</th>
                <th className="px-4 py-3 font-bold text-white border-b border-white/10 text-right">Amount Due</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {patientRecords.map((patient) => {
                const total = patient.total_charge || 0;
                const paid = patient.amount_paid || 0;
                const due = total - paid;
                return (
                  <tr key={patient.patient_id} className="hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs">{patient.patient_id}</td>
                    <td className="px-4 py-3 font-medium text-white">{patient.patient_name || "Unknown"}</td>
                    <td className="px-4 py-3 text-center">{patient.doctor_visits || 0}</td>
                    <td className="px-4 py-3 text-right">₹{(patient.room_charge || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">₹{(patient.lab_charge || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">₹{(patient.surgery_charge || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-300">₹{total.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-medium text-emerald-400">₹{paid.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-bold text-teal-400 bg-teal-500/5">₹{due.toLocaleString()}</td>
                  </tr>
                );
              })}
              {patientRecords.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No patient charges found in the current dataset.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>

    </div>
  );
}
